// src/features/task/EntityTaskPanel.tsx
// "Task" card for the detail area of another module's entity (document,
// shift, maintenance request, ...). Mirrors the calendar module's
// EntitySchedulePanel precedent (frontend/src/features/calendar/
// EntitySchedulePanel.tsx): a person driving a host entity's task lifecycle
// is looking at that entity, so the TaskAspect orchestration lives here
// instead of the task module (see docs/ux/task-ia.md §8).
//
// Covers the four entity-embedded RPCs the task IA placed outside the task
// module: AssignEntity, ChangeEntityTaskStatus, LogEntityTime and
// ListEntityActivity (the activity log doubles as the panel's only read —
// there is no GetEntityTask RPC, so "current state" is derived from the most
// recent status-change activity rather than fetched directly).
//
// Mounted today on the document detail page (`DocumentDetailPage.tsx`, below
// the backlinks section) as a "follow-up task" on the document — track who
// owns a review, its status, and time spent, without leaving the document.
// This repo has no generic host-entity page, so this is a single defensible
// host chosen per the design skill's placement logic; any other host entity
// with a TaskAspect (a staffing shift, a property maintenance request, ...)
// can reuse this panel the same way — pass its Entity in, nothing else to
// wire.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button, Badge, Alert } from '@react/components';
import { Select } from '@/components/Select';
import { AssignEntity } from '@core/api/endpoints/task/assign_entity';
import { ChangeEntityTaskStatus } from '@core/api/endpoints/task/change_entity_task_status';
import { LogEntityTime } from '@core/api/endpoints/task/log_entity_time';
import { ListEntityActivity } from '@core/api/endpoints/task/list_entity_activity';
import { getDisplayName, statusOptions, formatDateTime, taskStatusMap, getStatusMapping } from './logic';
import { TaskActivityActor, TaskActivityChange, TaskActivityTypeLabel } from './TaskActivityPresentation';
import { digitsOnly, errMsg, hoursMinutesToDuration } from './panelHelpers';
import type { TaskAspect } from '@core/types/task/TaskAspect';
import type { TaskActivity } from '@core/types/task/TaskActivity';
import type { Entity } from '@core/types/entity';

interface EntityTaskPanelProps {
  /** The host entity carrying the TaskAspect (document, shift, ...). */
  entity: Entity;
}

export function EntityTaskPanel({ entity }: EntityTaskPanelProps) {
  const { t, locale } = useI18n();

  const [task, setTask] = useState<TaskAspect | null>(null);
  const [activity, setActivity] = useState<TaskActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [statusOpen, setStatusOpen] = useState(false);
  const [nextStatus, setNextStatus] = useState('TASK_STATUS_OPEN');
  const [statusComment, setStatusComment] = useState('');

  const [assignOpen, setAssignOpen] = useState(false);
  const [assigneeId, setAssigneeId] = useState('');
  const [assigneeName, setAssigneeName] = useState('');

  const [timeOpen, setTimeOpen] = useState(false);
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [timeDescription, setTimeDescription] = useState('');

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await ListEntityActivity(entity.type_name, entity.id, { limit: 20 } as any);
      const items = res.activities?.activities ?? [];
      setActivity(items);
      const lastStatus = [...items].reverse().find((a) => a.activity_type === 'TASK_ACTIVITY_TYPE_STATUS_CHANGED');
      if (lastStatus?.new_value) {
        setTask((prev) => ({ ...(prev ?? ({} as TaskAspect)), status: lastStatus.new_value as any }));
      }
    } catch (e) {
      setError(errMsg(e, t('task.entityPanel.loadError')));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, [entity.type_name, entity.id]);

  const submitStatus = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await ChangeEntityTaskStatus(entity.type_name, entity.id, {
        target: entity,
        status: nextStatus as any,
        comment: statusComment.trim() || undefined,
      });
      setTask(res.task ?? task);
      setStatusOpen(false);
      setStatusComment('');
      await load();
    } catch (e) {
      setError(errMsg(e, t('task.entityPanel.statusError')));
    } finally {
      setBusy(false);
    }
  };

  const submitAssign = async () => {
    const id = assigneeId.trim();
    if (!id) {
      setError(t('task.assignControl.idRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await AssignEntity(entity.type_name, entity.id, {
        target: entity,
        assignee: { entity: { id }, display_name: assigneeName.trim() || undefined } as any,
      });
      setTask(res.task ?? task);
      setAssigneeId('');
      setAssigneeName('');
      setAssignOpen(false);
      await load();
    } catch (e) {
      setError(errMsg(e, t('task.assignControl.assignError')));
    } finally {
      setBusy(false);
    }
  };

  const submitTime = async () => {
    const h = parseInt(hours || '0', 10);
    const m = parseInt(minutes || '0', 10);
    if (h === 0 && m === 0) {
      setError(t('task.timeTracking.durationRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await LogEntityTime(entity.type_name, entity.id, {
        target: entity,
        duration: hoursMinutesToDuration(h, m),
        started_at: new Date().toISOString(),
        description: timeDescription.trim() || undefined,
      });
      setHours('');
      setMinutes('');
      setTimeDescription('');
      setTimeOpen(false);
      await load();
    } catch (e) {
      setError(errMsg(e, t('task.timeTracking.saveError')));
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) {
    return <div className="h-16 animate-pulse rounded-md bg-muted" aria-busy="true" />;
  }

  const status = task?.status;
  const statusInfo = status ? getStatusMapping(taskStatusMap, status) : null;

  return (
    <div className="space-y-3">
      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex items-center gap-2">
        {statusInfo ? (
          <Badge variant={statusInfo.variant} size="sm">{t(statusInfo.labelKey)}</Badge>
        ) : (
          <span className="text-sm text-muted-foreground">{t('task.entityPanel.noStatus')}</span>
        )}
        {task?.assignee && (
          <span className="text-sm text-foreground">{getDisplayName(task.assignee)}</span>
        )}
      </div>

      {!statusOpen && !assignOpen && !timeOpen && (
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" variant="secondary" onClick={() => setStatusOpen(true)}>
            {t('task.entityPanel.changeStatus')}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setAssignOpen(true)}>
            {t('task.assignControl.assign')}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setTimeOpen(true)}>
            {t('task.timeTracking.logTime')}
          </Button>
        </div>
      )}

      {statusOpen && (
        <div className="space-y-2 rounded-md border border-border p-2">
          <Select
            aria-label={t('task.entityPanel.changeStatus')}
            value={nextStatus}
            onChange={(e) => setNextStatus(e.target.value)}
          >
            {statusOptions.map((o) => (
              <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
            ))}
          </Select>
          <input
            type="text"
            aria-label={t('task.entityPanel.comment')}
            placeholder={t('task.entityPanel.commentPlaceholder')}
            value={statusComment}
            onChange={(e) => setStatusComment(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
          />
          <div className="flex gap-1.5">
            <Button size="sm" onClick={submitStatus} isLoading={busy}>{t('common.save')}</Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setStatusOpen(false)}>{t('common.cancel')}</Button>
          </div>
        </div>
      )}

      {assignOpen && (
        <div className="space-y-2 rounded-md border border-border p-2">
          <input
            type="text"
            aria-label={t('task.assignControl.userId')}
            placeholder={t('task.assignControl.userIdPlaceholder')}
            value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
          />
          <input
            type="text"
            aria-label={t('task.assignControl.userName')}
            placeholder={t('task.assignControl.userNamePlaceholder')}
            value={assigneeName}
            onChange={(e) => setAssigneeName(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
          />
          <div className="flex gap-1.5">
            <Button size="sm" onClick={submitAssign} isLoading={busy}>{t('task.assignControl.assign')}</Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setAssignOpen(false)}>{t('common.cancel')}</Button>
          </div>
        </div>
      )}

      {timeOpen && (
        <div className="space-y-2 rounded-md border border-border p-2">
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex items-center gap-1 text-sm text-foreground">
              <input
                type="text"
                inputMode="numeric"
                aria-label={t('task.timeTracking.hours')}
                placeholder="0"
                value={hours}
                onChange={(e) => setHours(digitsOnly(e.target.value))}
                className="w-14 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
              />
              {t('task.timeTracking.hoursShort')}
            </label>
            <label className="flex items-center gap-1 text-sm text-foreground">
              <input
                type="text"
                inputMode="numeric"
                aria-label={t('task.timeTracking.minutes')}
                placeholder="0"
                value={minutes}
                onChange={(e) => setMinutes(digitsOnly(e.target.value))}
                className="w-14 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
              />
              {t('task.timeTracking.minutesShort')}
            </label>
          </div>
          <input
            type="text"
            aria-label={t('task.field.description')}
            placeholder={t('task.timeTracking.descriptionPlaceholder')}
            value={timeDescription}
            onChange={(e) => setTimeDescription(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
          />
          <div className="flex gap-1.5">
            <Button size="sm" onClick={submitTime} isLoading={busy}>{t('task.timeTracking.save')}</Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setTimeOpen(false)}>{t('common.cancel')}</Button>
          </div>
        </div>
      )}

      {activity.length > 0 && (
        <div className="space-y-1.5 border-t border-border pt-2">
          {activity.slice(0, 5).map((a, idx) => (
            <div key={a.entity?.id || idx} className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground"><TaskActivityActor actor={a.actor} /></span>
              {' '}
              <TaskActivityTypeLabel type={a.activity_type} />
              {' · '}{formatDateTime(a.created_at, locale)}
              <TaskActivityChange activity={a} compact />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default EntityTaskPanel;
